import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import {
  LayoutDashboard, Users, FolderOpen, FileText,
  ClipboardList, Settings, LogOut, ChevronLeft, ChevronRight, Activity, Building2, ChevronsUpDown, Check, Shield
} from 'lucide-react';
import { motion } from 'framer-motion';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'hr', 'employee', 'public'] },
  { path: '/companies', label: 'Companies', icon: Building2, roles: ['admin', 'hr'] },
  { path: '/employees', label: 'Employees', icon: Users, roles: ['admin', 'hr'] },
  { path: '/pafs', label: 'PAF Files', icon: FolderOpen, roles: ['admin', 'hr', 'employee', 'public'] },
  { path: '/team', label: 'Team', icon: Users, roles: ['admin'] },
  { path: '/activity', label: 'Activity', icon: Activity, roles: ['admin', 'hr', 'employee'] },
  { path: '/audit-trail', label: 'Audit Trail', icon: ClipboardList, roles: ['admin', 'hr'] },
  { path: '/reports', label: 'Reports', icon: FileText, roles: ['admin', 'hr'] },
  { path: '/settings', label: 'Settings', icon: Settings, roles: ['admin', 'hr', 'employee'] },
  { path: '/admin', label: 'Admin Panel', icon: Shield, roles: ['admin'], superadminOnly: true },
];

export function Sidebar() {
  const { user, logout } = useAuth();
  const { companies, selectedCompanyId, setSelectedCompanyId, selectedCompany } = useCompany();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const userRole = user?.role || 'admin';
  const visibleNavItems = navItems.filter(item => {
    if (!item.roles.includes(userRole)) return false;
    if (item.superadminOnly && !user?.is_superadmin) return false;
    return true;
  });

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 72 : 260 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className="fixed left-0 top-0 h-screen bg-white border-r border-indigo-100/50 flex flex-col z-40 shadow-sm"
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="px-5 py-5 flex items-center gap-2">
        {collapsed ? (
          <img src="/logo.svg" alt="PAFHub" className="w-9 h-9 flex-shrink-0" />
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-2"
          >
            <img src="/logo.svg" alt="PAFHub" className="w-9 h-9 flex-shrink-0" />
            <span className="font-bold text-lg tracking-tight whitespace-nowrap"><span className="text-[#000000]">PAF</span><span className="text-[#4F46E5]">Hub</span></span>
          </motion.div>
        )}
      </div>

      {/* Company Selector */}
      {!collapsed && companies.length > 0 && (
        <div className="px-3 mb-2 relative">
          <button
            onClick={() => setCompanyDropdownOpen(!companyDropdownOpen)}
            data-testid="company-selector"
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-indigo-100/80 bg-indigo-50/30 hover:bg-indigo-50 transition-colors text-left"
          >
            <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-3.5 h-3.5 text-indigo-600" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-[#0F172A] truncate">
                {selectedCompany ? selectedCompany.name : 'All Companies'}
              </p>
              {selectedCompany?.ein && <p className="text-[10px] text-[#94A3B8] truncate">EIN: {selectedCompany.ein}</p>}
            </div>
            <ChevronsUpDown className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0" strokeWidth={1.5} />
          </button>

          {companyDropdownOpen && (
            <>
              <div className="fixed inset-0 z-50" onClick={() => setCompanyDropdownOpen(false)} />
              <div className="absolute left-3 right-3 top-full mt-1 bg-white rounded-xl border border-indigo-100 shadow-lg shadow-indigo-100/50 z-50 py-1 max-h-60 overflow-y-auto">
                <button
                  onClick={() => { setSelectedCompanyId(''); setCompanyDropdownOpen(false); }}
                  data-testid="company-option-all"
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-indigo-50 transition-colors ${!selectedCompanyId ? 'bg-indigo-50' : ''}`}
                >
                  <div className="w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-3 h-3 text-slate-500" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-medium text-[#0F172A] flex-1">All Companies</span>
                  {!selectedCompanyId && <Check className="w-3.5 h-3.5 text-indigo-600" strokeWidth={2} />}
                </button>
                {companies.map(c => (
                  <button
                    key={c.id}
                    onClick={() => { setSelectedCompanyId(c.id); setCompanyDropdownOpen(false); }}
                    data-testid={`company-option-${c.id}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-indigo-50 transition-colors ${selectedCompanyId === c.id ? 'bg-indigo-50' : ''}`}
                  >
                    <div className="w-6 h-6 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-3 h-3 text-indigo-600" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#0F172A] truncate">{c.name}</p>
                      {c.ein && <p className="text-[10px] text-[#94A3B8]">EIN: {c.ein}</p>}
                    </div>
                    {selectedCompanyId === c.id && <Check className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" strokeWidth={2} />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">
        {visibleNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 ${
                isActive
                  ? 'bg-indigo-50 text-indigo-600 shadow-sm shadow-indigo-100/50'
                  : 'text-[#64748B] hover:text-[#0F172A] hover:bg-slate-50'
              } ${collapsed ? 'justify-center' : ''}`
            }
          >
            <item.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
            {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User + collapse */}
      <div className="p-3 border-t border-indigo-50 space-y-2">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center p-2 rounded-xl text-[#94A3B8] hover:text-[#0F172A] hover:bg-slate-50 transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
        {!collapsed && user && (
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm">
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0F172A] truncate">{user.name}</p>
              <p className="text-xs text-[#94A3B8] truncate capitalize">{user.role?.replace('_', ' ')}</p>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium text-[#94A3B8] hover:text-red-500 hover:bg-red-50 transition-colors ${collapsed ? 'justify-center' : ''}`}
        >
          <LogOut className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>
    </motion.aside>
  );
}
